// HeroEditor.tsx 
import React from "react";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  Alert,
} from "@mui/material";
import type { HeroSettings } from "../../api/adminHeroApi";

type Props = {
  value: Partial<HeroSettings>;
  onChange: (next: Partial<HeroSettings>) => void;
};

export default function HeroEditor({ value, onChange }: Props) {
  const v = value;

  const set = (k: keyof HeroSettings, next: any) => onChange({ ...v, [k]: next });

  // Helper to convert ISO to datetime-local format (YYYY-MM-DDThh:mm)
  // This preserves the local time that was originally selected
  const isoToDatetimeLocal = (isoString?: string | null): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';

      // Get local date components from the ISO string
      // This ensures we display the same time that was originally selected
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Helper to convert datetime-local to ISO string - FIXED VERSION
  const datetimeLocalToIso = (datetimeLocal: string): string | null => {
    if (!datetimeLocal) return null;
    try {
      // Parse the datetime-local string (which is in local time)
      const [year, month, day, hour, minute] = [
        parseInt(datetimeLocal.slice(0, 4)),
        parseInt(datetimeLocal.slice(5, 7)) - 1, // Month is 0-indexed in JS
        parseInt(datetimeLocal.slice(8, 10)),
        parseInt(datetimeLocal.slice(11, 13)),
        parseInt(datetimeLocal.slice(14, 16))
      ];

      // Create a date in the local timezone
      const localDate = new Date(year, month, day, hour, minute);

      // Return ISO string which will be in UTC but represents the same moment in time
      return localDate.toISOString();
    } catch (error) {
      console.error('Error parsing datetime-local:', error);
      return null;
    }
  };

  // Helper to format date for display in alerts
  const formatDisplayDate = (isoString?: string | null): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch {
      return '';
    }
  };

  // Get current time for display
  const currentTime = new Date().toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });

  // Check if dates are valid for status alerts
  const now = new Date();
  const startDate = v.start_at ? new Date(v.start_at) : null;
  const endDate = v.end_at ? new Date(v.end_at) : null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Hero Details
        </Typography>

        <Stack spacing={2}>
          {/* Current time display for admin reference */}
          <Alert severity="info" sx={{ mb: 1 }}>
            Current device time: <strong>{currentTime}</strong>
          </Alert>

          <TextField
            label="Headline"
            value={v.headline || ""}
            onChange={(e) => set("headline", e.target.value)}
            fullWidth
          />
          <TextField
            label="Subheadline"
            value={v.subheadline || ""}
            onChange={(e) => set("subheadline", e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          <FormControl fullWidth>
            <InputLabel>Layout Type</InputLabel>
            <Select
              label="Layout Type"
              value={v.hero_layout_type || "hero"}
              onChange={(e) => set("hero_layout_type", e.target.value)}
            >
              <MenuItem value="hero">hero</MenuItem>
              <MenuItem value="banner">banner</MenuItem>
              <MenuItem value="mini_banner">mini banner</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="subtitle1" fontWeight={700} mt={2}>
            Layout Controls
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Content Align</InputLabel>
              <Select
                label="Content Align"
                value={(v as any).content_align || "left"}
                onChange={(e) =>
                  set("content_align" as keyof typeof v, e.target.value)
                }
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Content Position</InputLabel>
              <Select
                label="Content Position"
                value={(v as any).content_position || "left"}
                onChange={(e) =>
                  set("content_position" as keyof typeof v, e.target.value)
                }
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Media Position</InputLabel>
              <Select
                label="Media Position"
                value={(v as any).media_position || "right"}
                onChange={(e) =>
                  set("media_position" as keyof typeof v, e.target.value)
                }
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Banner Height</InputLabel>
              <Select
                label="Banner Height"
                value={(v as any).banner_height || "standard"}
                onChange={(e) =>
                  set("banner_height" as keyof typeof v, e.target.value)
                }
              >
                <MenuItem value="compact">Compact</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="tall">Tall</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Background Type</InputLabel>
              <Select
                label="Background Type"
                value={v.background_type || "image"}
                onChange={(e) => set("background_type", e.target.value)}
              >
                <MenuItem value="image">image</MenuItem>
                <MenuItem value="video">video</MenuItem>
                <MenuItem value="gradient">gradient</MenuItem>
              </Select>
            </FormControl>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Placement</InputLabel>
                <Select
                  label="Placement"
                  value={(v as any).placement || "home"}
                  onChange={(e) => set("placement" as keyof HeroSettings, e.target.value)}
                >
                  <MenuItem value="home">Homepage</MenuItem>
                  <MenuItem value="shop">Shop</MenuItem>
                  <MenuItem value="checkout">Checkout</MenuItem>
                  <MenuItem value="global">Global</MenuItem>
                  <MenuItem value="wholesale_home">Wholesale Home</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Visibility</InputLabel>
                <Select
                  label="Visibility"
                  value={(v as any).visibility || "public"}
                  onChange={(e) => set("visibility" as keyof HeroSettings, e.target.value)}
                >
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="members">Members only</MenuItem>
                  <MenuItem value="guests">Guests only</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Display Priority"
                value={(v as any).display_priority ?? 0}
                onChange={(e) =>
                  set("display_priority" as keyof HeroSettings, Number(e.target.value || 0))
                }
                type="number"
                fullWidth
                helperText="Higher priority shows first"
              />
            </Stack>

            <TextField
              label="Overlay Opacity (0..1)"
              value={v.overlay_opacity ?? 0.45}
              onChange={(e) => set("overlay_opacity", Number(e.target.value))}
              type="number"
              inputProps={{ step: "0.05", min: 0, max: 1 }}
              fullWidth
            />
          </Stack>

          {(v.background_type || "image") !== "gradient" ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Background URL"
                value={v.background_url || ""}
                onChange={(e) => set("background_url", e.target.value)}
                fullWidth
              />
              <TextField
                label="Mobile Background URL (optional)"
                value={v.background_url_mobile || ""}
                onChange={(e) => set("background_url_mobile", e.target.value)}
                fullWidth
              />
            </Stack>
          ) : (
            <TextField
              label='Gradient CSS (e.g. "linear-gradient(135deg, #111, #333)")'
              value={v.gradient_css || ""}
              onChange={(e) => set("gradient_css", e.target.value)}
              fullWidth
            />
          )}

          <Typography variant="subtitle1" fontWeight={700}>
            CTAs
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Primary CTA Text (optional)"
              value={v.primary_cta_text ?? ""}
              onChange={(e) => set("primary_cta_text", e.target.value || null)}
              fullWidth
            />
            <TextField
              label="Primary CTA Link (optional)"
              value={v.primary_cta_link ?? ""}
              onChange={(e) => set("primary_cta_link", e.target.value || null)}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Secondary CTA Text (optional)"
              value={v.secondary_cta_text || ""}
              onChange={(e) => set("secondary_cta_text", e.target.value || null)}
              fullWidth
            />
            <TextField
              label="Secondary CTA Link (optional)"
              value={v.secondary_cta_link || ""}
              onChange={(e) => set("secondary_cta_link", e.target.value || null)}
              fullWidth
            />
          </Stack>

          <Typography variant="subtitle1" fontWeight={700}>
            Promo
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={!!v.promo_enabled}
                onChange={(e) => set("promo_enabled", e.target.checked)}
              />
            }
            label="Promo Enabled"
          />

          <TextField
            label="Promo Text"
            value={v.promo_text || ""}
            onChange={(e) => set("promo_text", e.target.value)}
            fullWidth
            disabled={!v.promo_enabled}
          />

          <Typography variant="subtitle1" fontWeight={700}>
            Toggles
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!v.loyalty_highlight}
                  onChange={(e) => set("loyalty_highlight", e.target.checked)}
                />
              }
              label="Highlight Loyalty Block"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!(v as any).carousel_enabled}
                  onChange={(e) =>
                    set("carousel_enabled" as keyof HeroSettings, e.target.checked)
                  }
                />
              }
              label="Include in carousel"
            />

            <FormControlLabel
              control={
                <Switch checked={!!v.is_active} onChange={(e) => set("is_active", e.target.checked)} />
              }
              label="Active"
            />
          </Stack>

          <Typography variant="subtitle1" fontWeight={700}>
            Scheduling
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Start At"
              type="datetime-local"
              value={isoToDatetimeLocal(v.start_at)}
              onChange={(e) => set("start_at", datetimeLocalToIso(e.target.value))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="When should this hero start showing? (your device time)"
            />
            <TextField
              label="End At"
              type="datetime-local"
              value={isoToDatetimeLocal(v.end_at)}
              onChange={(e) => set("end_at", datetimeLocalToIso(e.target.value))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="When should this hero stop showing? (your device time)"
            />
          </Stack>

          {/* Show scheduling status with proper timezone display */}
          {startDate && endDate && startDate < now && endDate > now && (
            <Alert severity="success">
              This hero is currently active (started: {formatDisplayDate(v.start_at)})
            </Alert>
          )}
          {startDate && startDate > now && (
            <Alert severity="info">
              This hero is scheduled to start on {formatDisplayDate(v.start_at)}
            </Alert>
          )}
          {endDate && endDate < now && (
            <Alert severity="warning">
              This hero expired on {formatDisplayDate(v.end_at)}
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}